import { useEffect, useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import {
  Card, CardContent,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Megaphone, CheckCircle2, Loader2, Newspaper, Tv2, Globe2,
  Sparkles, ShieldCheck, Award, ArrowRight, Building2,
  Quote, Edit3, Send, Video, MapPin, FileText,
} from "lucide-react";
import { useLocationStore } from "@/lib/store";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import type { Location, CitationOrder } from "@shared/schema";

const OUTLET_LOGOS = [
  "Fox", "NBC", "CBS", "ABC", "Yahoo Finance", "Digital Journal",
  "MarketWatch", "Benzinga", "Street Insider", "Google News",
];

const VALUE_PROPS = [
  {
    icon: Globe2,
    title: "Syndicate to 400+ news outlets",
    body:
      "One release, one fee — your story lands on Fox, NBC, CBS, ABC and hundreds more affiliated news sites through our premier newswire.",
  },
  {
    icon: Award,
    title: "High-trust, do-follow backlinks",
    body:
      "Every placement is a real news domain — exactly the kind of authority signal Google rewards in the Map Pack and AI Overviews.",
  },
  {
    icon: ShieldCheck,
    title: "Tested & proven for local SEO",
    body:
      "We use this exact press release service for our own brands. Sustained featured-snippet appearances, organic keyword gains, and Map Pack lift.",
  },
  {
    icon: Sparkles,
    title: "Custom-written by a senior writer",
    body:
      "No templates. A real human writes, edits, and tightens your release until the tone matches your brand. Unlimited revisions before syndication.",
  },
  {
    icon: Video,
    title: "Embed video + map for free",
    body:
      "Strengthen YouTube and local relevance signals. Each release supports one video embed and one Google Map embed — no extra charge.",
  },
  {
    icon: MapPin,
    title: "Brand reputation control",
    body:
      "Press releases shape what shows up when people Google your name — pushing positive, branded news into the first page of results.",
  },
];

const DELIVERABLES = [
  "Custom-written press release (400–600 words) by a senior news writer",
  "Unlimited edits and rewrites until you approve every word",
  "Syndication to 400+ authority news outlets through a premium newswire",
  "Verified placements on top-tier sites like Fox, NBC, CBS, ABC affiliates",
  "Optional video embed (YouTube) and Google Map embed at no extra cost",
  "Branded anchor + naked-URL backlinks pointing to your GBP and website",
  "Full clip report with live placement URLs delivered within 7–10 days",
  "White-labeled — your client never sees Digital Domination on the byline",
];

const PROCESS_STEPS = [
  {
    icon: FileText,
    step: "1",
    title: "You give us the angle",
    body:
      "Tell us what's newsworthy — a launch, a milestone, an award, a community story, a partnership. Anything we can build a real news angle around.",
  },
  {
    icon: Edit3,
    step: "2",
    title: "Senior writer drafts your release",
    body:
      "A human writer crafts the release in proper AP style and sends you a Google Doc. Edit, rewrite, push back — unlimited revisions, no clock.",
  },
  {
    icon: Send,
    step: "3",
    title: "We syndicate across 400+ outlets",
    body:
      "Once you approve, we push the release through our premium newswire. Placements go live across Fox, NBC, CBS, ABC affiliates and more.",
  },
  {
    icon: Newspaper,
    step: "4",
    title: "You get a full clip report",
    body:
      "Within 7–10 days you receive a live link report — every URL where your release was published, ready to drop into a client deck.",
  },
];

const ANGLE_PRESETS: { value: string; label: string; hint: string }[] = [
  {
    value: "new_store",
    label: "New store / location",
    hint: "Opening date, address, what's special about the new spot, any grand-opening offer.",
  },
  {
    value: "new_service",
    label: "New service or offering",
    hint: "What the service is, who it's for, why you launched it, pricing if shareable.",
  },
  {
    value: "new_offer",
    label: "New offer or promotion",
    hint: "What's the deal, who qualifies, dates it runs, what's the catch (or lack of one).",
  },
  {
    value: "service_area_expansion",
    label: "Service area expansion",
    hint: "New cities/zip codes you now serve, why you expanded, any local hires or partners.",
  },
  {
    value: "award_recognition",
    label: "Award or recognition",
    hint: "Award name, who issued it, what it's for, link to source if public.",
  },
  {
    value: "community_partnership",
    label: "Community partnership or sponsorship",
    hint: "Who you partnered with, the cause/event, dates, what you're doing together.",
  },
  {
    value: "milestone_anniversary",
    label: "Milestone or anniversary",
    hint: "What milestone (years in business, customers served, jobs completed), the story behind it.",
  },
  {
    value: "industry_insight",
    label: "Industry insight or trend",
    hint: "The trend or insight, why it matters locally, your take or data backing it up.",
  },
  {
    value: "other",
    label: "Other (write your own angle)",
    hint: "Tell us the story in your own words — anything newsworthy works.",
  },
];

const FAQ_ITEMS = [
  {
    q: "Will Google penalize me for a press release link?",
    a: "No. These are real news placements on real news domains — the kind of links Google explicitly rewards. We don't use group-buy networks or PBNs. The release is distributed through the same premium newswire used by Fortune 500 PR teams.",
  },
  {
    q: "What kind of business gets the most lift?",
    a: "Local service businesses with a real story to tell — a new location, a new service, a community partnership, an award, a milestone. The more authentic the angle, the more outlets pick it up.",
  },
  {
    q: "How fast will I rank?",
    a: "Most placements go live within 24–72 hours of distribution. SEO lift (Map Pack movement, branded SERP control, AI Overview citations) compounds over 30–60 days as Google re-crawls the news domains.",
  },
  {
    q: "Can I write my own release?",
    a: "You can — but you don't have to. A senior writer is included in the $599 fee. If you'd rather draft it yourself, send us a doc and we'll edit, polish, and syndicate it.",
  },
  {
    q: "Do you provide a clip report?",
    a: "Yes. Within 7–10 days you receive a live URL report showing every news outlet that picked up your release. Perfect for client decks and reputation management.",
  },
  {
    q: "Is this white-labeled for agencies?",
    a: "Yes. The release ships under your client's brand. Nothing on the byline, in the footer, or in the report mentions Digital Domination.",
  },
];

export default function PressReleasePage() {
  const { selectedLocationId, setSelectedLocationId } = useLocationStore();
  const { toast } = useToast();

  const { data: locations = [] } = useQuery<Location[]>({
    queryKey: ["/api/locations"],
  });

  const locationId = selectedLocationId || locations[0]?.id || "";

  const { data: orders = [] } = useQuery<CitationOrder[]>({
    queryKey: ["/api/citation-orders", locationId],
    enabled: !!locationId,
  });

  const paidReleaseCount = orders.filter(
    (o) => o.packageType === "press_release" && o.status === "paid",
  ).length;

  const [angleType, setAngleType] = useState<string>("");
  const [angleDetails, setAngleDetails] = useState<string>("");
  const selectedPreset = ANGLE_PRESETS.find((p) => p.value === angleType);
  const detailsRequired = angleType === "other";
  const detailsOk = !detailsRequired || angleDetails.trim().length > 0;

  const searchParams = new URLSearchParams(window.location.search);
  const sessionId = searchParams.get("session_id");

  useEffect(() => {
    if (!sessionId) return;
    apiRequest("GET", `/api/citation-orders/verify-session?session_id=${sessionId}`)
      .then((r) => r.json())
      .then((order) => {
        if (order?.status === "paid") {
          toast({
            title: "Press release ordered",
            description:
              "Payment confirmed. A senior writer will reach out within one business day to start your release.",
          });
          queryClient.invalidateQueries({ queryKey: ["/api/citation-orders", locationId] });
        }
        window.history.replaceState({}, "", "/press-release");
      })
      .catch(() => {
        window.history.replaceState({}, "", "/press-release");
      });
  }, [sessionId, locationId, toast]);

  const checkout = useMutation({
    mutationFn: async () => {
      if (!locationId) throw new Error("Pick a location first");
      if (!selectedPreset) throw new Error("Pick a press release angle first");
      if (detailsRequired && !angleDetails.trim()) {
        throw new Error("Tell us a bit about your custom angle");
      }
      const customerNotes = [
        `Angle: ${selectedPreset.label}`,
        angleDetails.trim().length > 0
          ? `Details: ${angleDetails.trim()}`
          : "Details: (none provided — writer will reach out for specifics)",
      ].join("\n\n");
      const res = await apiRequest("POST", "/api/citation-orders/checkout", {
        locationId,
        packageType: "press_release",
        customerNotes,
      });
      return res.json();
    },
    onSuccess: (data: any) => {
      if (data.url) window.location.href = data.url;
    },
    onError: (err: Error) => {
      toast({
        title: "Checkout failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const buyDisabled =
    !locationId || !selectedPreset || !detailsOk || checkout.isPending;

  return (
    <div
      className="container max-w-6xl py-8 px-6 md:px-10 space-y-12 overflow-y-auto h-full pb-24"
      data-testid="container-press-release"
    >
      {/* HERO */}
      <section className="space-y-6" data-testid="section-hero">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-brand-orange/25 to-orange-500/15 flex items-center justify-center">
            <Megaphone className="w-5 h-5 text-brand-orange" />
          </div>
          <Badge variant="outline" className="border-brand-orange/40 text-brand-orange">
            Authority Boost
          </Badge>
        </div>
        <h1
          className="text-4xl md:text-5xl font-extrabold leading-tight max-w-4xl"
          data-testid="text-hero-headline"
        >
          Get your business on{" "}
          <span className="text-brand-orange">Fox, NBC, CBS</span> and 400+ news
          outlets — written and syndicated for you.
        </h1>
        <p
          className="text-lg text-muted-foreground max-w-3xl"
          data-testid="text-hero-sub"
        >
          A custom press release written by a senior news writer, distributed
          through a premium newswire. Real news placements, high-trust backlinks,
          and the SEO authority lift Google rewards in the Map Pack and AI
          Overviews.
        </p>

        <div className="flex flex-col md:flex-row gap-4 md:items-stretch">
          <div
            className="flex-1 rounded-xl border-2 border-brand-orange/40 bg-gradient-to-br from-brand-orange/10 via-background to-brand-blue/10 p-5 space-y-3 shadow-lg shadow-brand-orange/10"
            data-testid="panel-hero-buy"
          >
            <div className="flex items-baseline justify-between flex-wrap gap-2">
              <div className="text-2xl font-bold">
                <span className="text-brand-orange" data-testid="text-hero-price">
                  $599
                </span>
                <span className="text-base font-normal text-muted-foreground ml-2">
                  one-time, all-inclusive
                </span>
              </div>
              <Badge
                variant="outline"
                className="border-brand-orange/40 text-brand-orange text-[10px]"
              >
                Writing + Syndication
              </Badge>
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="select-location-hero"
                className="text-xs uppercase tracking-wider text-muted-foreground"
              >
                Location
              </Label>
              <Select value={locationId} onValueChange={setSelectedLocationId}>
                <SelectTrigger
                  id="select-location-hero"
                  className="w-full"
                  data-testid="select-location-hero"
                >
                  <SelectValue placeholder="Choose a location" />
                </SelectTrigger>
                <SelectContent>
                  {locations.map((l) => (
                    <SelectItem
                      key={l.id}
                      value={l.id}
                      data-testid={`option-location-hero-${l.id}`}
                    >
                      {l.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="select-angle"
                className="text-xs uppercase tracking-wider text-muted-foreground"
              >
                Press release angle
              </Label>
              <Select value={angleType} onValueChange={setAngleType}>
                <SelectTrigger
                  id="select-angle"
                  className="w-full"
                  data-testid="select-angle"
                >
                  <SelectValue placeholder="Pick a preconfigured angle…" />
                </SelectTrigger>
                <SelectContent>
                  {ANGLE_PRESETS.map((p) => (
                    <SelectItem
                      key={p.value}
                      value={p.value}
                      data-testid={`option-angle-${p.value}`}
                    >
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1.5">
              <Label
                htmlFor="textarea-angle-details"
                className="text-xs uppercase tracking-wider text-muted-foreground flex items-center justify-between gap-2"
              >
                <span>
                  Tell us about it{" "}
                  <span className="text-muted-foreground/70 normal-case tracking-normal">
                    {detailsRequired ? "(required)" : "(optional but recommended)"}
                  </span>
                </span>
              </Label>
              <Textarea
                id="textarea-angle-details"
                value={angleDetails}
                onChange={(e) => setAngleDetails(e.target.value)}
                placeholder={
                  selectedPreset
                    ? selectedPreset.hint
                    : "Pick an angle above, then add the specifics our writer will need."
                }
                rows={4}
                maxLength={4000}
                className="w-full text-sm bg-background/50"
                data-testid="textarea-angle-details"
              />
              <div className="flex items-center justify-between text-[10px] text-muted-foreground/70">
                <span>Goes straight to your senior writer.</span>
                <span data-testid="text-angle-char-count">
                  {angleDetails.length}/4000
                </span>
              </div>
            </div>

            <Button
              size="lg"
              onClick={() => checkout.mutate()}
              disabled={buyDisabled}
              className="w-full bg-gradient-to-r from-brand-orange to-orange-600 hover:from-brand-orange/90 hover:to-orange-700 text-white font-semibold shadow-lg shadow-brand-orange/20"
              data-testid="button-hero-cta"
            >
              {checkout.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Megaphone className="w-4 h-4 mr-2" />
              )}
              Order my press release — $599
            </Button>
            <div className="flex items-center justify-between text-xs text-muted-foreground gap-3">
              <span data-testid="text-hero-helper">
                {!locationId
                  ? "Pick a location to enable checkout."
                  : !selectedPreset
                    ? "Pick an angle above to enable checkout."
                    : detailsRequired && !angleDetails.trim()
                      ? "Add a few details about your custom angle to continue."
                      : paidReleaseCount > 0
                        ? `${paidReleaseCount} release${paidReleaseCount === 1 ? "" : "s"} already ordered for this location — order another anytime.`
                        : "Senior writer + premium newswire syndication. 7–10 day turnaround."}
              </span>
            </div>
          </div>
        </div>

        {/* outlet ribbon */}
        <div
          className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs uppercase tracking-[0.2em] text-muted-foreground/70"
          data-testid="ribbon-outlets"
        >
          <span className="text-brand-orange/80 font-semibold">Featured on:</span>
          {OUTLET_LOGOS.map((name) => (
            <span
              key={name}
              className="flex items-center gap-1"
              data-testid={`outlet-${name.toLowerCase().replace(/\s+/g, "-")}`}
            >
              <Tv2 className="w-3 h-3" />
              {name}
            </span>
          ))}
        </div>
      </section>

      {/* WHY IT WORKS */}
      <section className="space-y-4" data-testid="section-why">
        <div className="flex items-center gap-2">
          <Sparkles className="w-5 h-5 text-brand-orange" />
          <h2 className="text-2xl font-bold">
            Press releases aren't just for publicity — they're a ranking weapon
          </h2>
        </div>
        <p className="text-base text-muted-foreground max-w-3xl">
          Most agencies treat press releases like an afterthought. We've tested
          them as an SEO play across hundreds of local businesses, and the data
          is clear: a properly written and syndicated release drives sustained
          featured-snippet appearances, organic keyword gains, and Map Pack
          lift. Press releases shape your branded SERP, control the narrative,
          and earn high-trust links from real news domains — exactly what
          Google's local algorithm rewards.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {VALUE_PROPS.map((v) => {
            const Icon = v.icon;
            return (
              <Card
                key={v.title}
                className="border border-brand-orange/15 bg-gradient-to-br from-background via-background to-brand-orange/[0.04]"
                data-testid={`card-value-${v.title.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")}`}
              >
                <CardContent className="p-5 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg bg-brand-orange/15 flex items-center justify-center">
                      <Icon className="w-4 h-4 text-brand-orange" />
                    </div>
                    <h3 className="text-sm font-semibold text-white">
                      {v.title}
                    </h3>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {v.body}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* WHAT YOU GET */}
      <section className="space-y-4" data-testid="section-deliverables">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="w-5 h-5 text-brand-orange" />
          <h2 className="text-2xl font-bold">What's included for $599</h2>
        </div>
        <Card className="border-2 border-brand-orange/30 bg-gradient-to-br from-background via-background to-brand-orange/5">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {DELIVERABLES.map((d, i) => (
                <div
                  key={i}
                  className="flex items-start gap-3"
                  data-testid={`deliverable-${i}`}
                >
                  <CheckCircle2 className="w-4 h-4 text-brand-orange mt-0.5 shrink-0" />
                  <span className="text-sm text-white/85">{d}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* HOW IT WORKS */}
      <section className="space-y-4" data-testid="section-process">
        <div className="flex items-center gap-2">
          <ArrowRight className="w-5 h-5 text-brand-orange" />
          <h2 className="text-2xl font-bold">How it works</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          {PROCESS_STEPS.map((s) => {
            const Icon = s.icon;
            return (
              <Card
                key={s.step}
                className="border border-white/10 bg-white/[0.02]"
                data-testid={`step-${s.step}`}
              >
                <CardContent className="p-5 space-y-2">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-brand-orange/15 border border-brand-orange/30 flex items-center justify-center text-brand-orange font-bold text-sm">
                      {s.step}
                    </div>
                    <Icon className="w-4 h-4 text-brand-orange/80" />
                  </div>
                  <h3 className="text-sm font-semibold text-white">
                    {s.title}
                  </h3>
                  <p className="text-xs text-muted-foreground leading-relaxed">
                    {s.body}
                  </p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      {/* QUOTE / TRUST */}
      <section data-testid="section-quote">
        <Card className="border border-brand-blue/20 bg-gradient-to-br from-brand-blue/5 via-background to-brand-orange/5">
          <CardContent className="p-6 md:p-8 flex flex-col md:flex-row gap-5 items-start">
            <Quote className="w-8 h-8 text-brand-blue/60 shrink-0" />
            <div className="space-y-3">
              <p className="text-base md:text-lg text-white/90 leading-relaxed">
                We use this exact press release service on our own brands and
                our highest-tier client accounts. It's the single fastest way
                to push fresh, high-trust authority into a Google Business
                Profile — and to lock down branded SERP control before a
                competitor does.
              </p>
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Building2 className="w-4 h-4 text-brand-orange/70" />
                Digital Domination — In-House SEO Team
              </div>
            </div>
          </CardContent>
        </Card>
      </section>

      {/* FAQ */}
      <section className="space-y-4" data-testid="section-faq">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-brand-orange" />
          <h2 className="text-2xl font-bold">Frequently asked questions</h2>
        </div>
        <div className="space-y-3">
          {FAQ_ITEMS.map((f, i) => (
            <Card
              key={i}
              className="border border-white/10 bg-white/[0.02]"
              data-testid={`faq-${i}`}
            >
              <CardContent className="p-5 space-y-1.5">
                <h3 className="text-sm font-semibold text-white">{f.q}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">
                  {f.a}
                </p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section data-testid="section-final-cta">
        <Card className="border-2 border-brand-orange/40 bg-gradient-to-br from-brand-orange/10 via-background to-brand-blue/10 shadow-xl shadow-brand-orange/10">
          <CardContent className="p-6 md:p-8 flex flex-col md:flex-row gap-5 items-center justify-between">
            <div className="space-y-1">
              <h3 className="text-xl md:text-2xl font-bold text-white">
                Ready to get featured on Fox, NBC and CBS?
              </h3>
              <p className="text-sm text-muted-foreground max-w-2xl">
                $599. Custom-written by a senior news writer. Syndicated to 400+
                outlets through a premium newswire. Full clip report in 7–10
                days. White-labeled for agencies.
              </p>
              {selectedPreset ? (
                <p
                  className="text-xs text-brand-orange/90 pt-1"
                  data-testid="text-final-angle-summary"
                >
                  Angle: <span className="font-semibold">{selectedPreset.label}</span>
                </p>
              ) : (
                <p
                  className="text-xs text-muted-foreground/70 pt-1"
                  data-testid="text-final-angle-empty"
                >
                  Scroll up to pick your press release angle before checkout.
                </p>
              )}
            </div>
            <Button
              size="lg"
              onClick={() => checkout.mutate()}
              disabled={buyDisabled}
              className="bg-gradient-to-r from-brand-orange to-orange-600 hover:from-brand-orange/90 hover:to-orange-700 text-white font-semibold shadow-lg shadow-brand-orange/20 shrink-0"
              data-testid="button-final-cta"
            >
              {checkout.isPending ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Megaphone className="w-4 h-4 mr-2" />
              )}
              Order my press release — $599
            </Button>
          </CardContent>
        </Card>
      </section>
    </div>
  );
}
